/**
 * SESAAT — Marketplace Service
 * Firestore + Storage + callable wrappers for the Marketplace feature.
 *
 * Collections:
 *   marketplace_products/{id}                    — public product (no secrets)
 *   marketplace_products/{id}/private/data       — ebook file path + internal notes (admin only)
 *   marketplace_products/{id}/stock_items/{iid}  — satu akaun sebenar setiap doc (admin only)
 *   marketplace_orders/{id}                      — one purchase record
 *   marketplace_orders/{id}/delivery/data        — delivered access/credential text (buyer+admin)
 *
 * IMPORTANT — affiliateRate is stored as a FRACTION (0–1), e.g. 0.40 for 40%,
 * to match affiliate_config/settings.commissionRate. The admin form converts the
 * percent the user types into a fraction before calling here.
 */

import { db, auth, functions, storage } from '../firebase';
import {
  doc, getDoc, setDoc, addDoc, updateDoc, deleteDoc, writeBatch,
  collection, query, where, orderBy, getDocs, serverTimestamp,
} from 'firebase/firestore';
import { ref as storageRef, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { httpsCallable } from 'firebase/functions';
import { getRefCookie } from './affiliateService';

const PRODUCTS = 'marketplace_products';
const ORDERS = 'marketplace_orders';

// ═══════════════════════════════════════════
// UI METADATA (shared by admin + buyer views)
// ═══════════════════════════════════════════

export const CATEGORIES = [
  { id: 'ebook', label: 'Ebook', defaultFulfillment: 'auto_download' },
  { id: 'tiktok_account', label: 'Akaun TikTok', defaultFulfillment: 'auto_stock' },
  { id: 'flow_ultra', label: 'Flow Ultra', defaultFulfillment: 'auto_stock' },
];
export const FULFILLMENTS = ['auto_download', 'auto_stock', 'manual'];
export const FULFILLMENT_LABELS = {
  auto_download: 'Auto download',
  auto_stock: 'Auto serah akaun (dari stok)',
  manual: 'Admin serah manual',
};
export const PRICING_MODES = ['free', 'paid'];
export const STATUSES = ['draft', 'published', 'sold_out', 'archived'];

export function categoryLabel(id) {
  return CATEGORIES.find((c) => c.id === id)?.label || id;
}

/** Format sen (integer) into a MYR string, e.g. 4900 -> "RM49.00" */
export function formatPrice(sen) {
  const n = Number(sen) || 0;
  return `RM${(n / 100).toFixed(2)}`;
}

function fileExt(file, fallback = 'jpg') {
  const m = (file?.name || '').match(/\.([a-zA-Z0-9]+)$/);
  return m ? m[1].toLowerCase() : fallback;
}

function adminEmail() {
  return auth.currentUser?.email || '';
}

// ═══════════════════════════════════════════
// PRODUCTS — read
// ═══════════════════════════════════════════

/** Published + sold-out products for the buyer marketplace (ordered).
 *  ⭐ Sold-out DISERTAKAN (arahan user 2026-08-21): produk habis stok tetap
 *  dipapar dengan lencana "SOLD OUT" — hanya `draft` (belum terbit) yang
 *  disorok. `in` memakai indeks komposit SAMA (status, order) seperti `==`,
 *  jadi tiada indeks baharu diperlukan. ⚠️ Peraturan Firestore MESTI benarkan
 *  membaca `sold_out` juga (lihat firestore.rules marketplace_products) — kalau
 *  tidak query ini DITOLAK sepenuhnya, bukan sekadar menapis keluar sold_out. */
export async function listPublishedProducts() {
  const q = query(
    collection(db, PRODUCTS),
    where('status', 'in', ['published', 'sold_out']),
    orderBy('order', 'asc'),
  );
  const snap = await getDocs(q);
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}

/** Single product (public fields only — secrets live in /private). */
export async function getProduct(productId) {
  const snap = await getDoc(doc(db, PRODUCTS, productId));
  return snap.exists() ? { id: snap.id, ...snap.data() } : null;
}

/** All products including drafts — admin only (rules enforce). */
export async function adminListProducts() {
  const q = query(collection(db, PRODUCTS), orderBy('order', 'asc'));
  const snap = await getDocs(q);
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}

/** Admin: read the private subdoc (ebook path + notes). */
export async function adminGetProductPrivate(productId) {
  const snap = await getDoc(doc(db, PRODUCTS, productId, 'private', 'data'));
  return snap.exists() ? snap.data() : null;
}

// ═══════════════════════════════════════════
// PRODUCTS — admin write
// ═══════════════════════════════════════════

/**
 * Create a product. `data.price`/`originalPrice` are in sen (int).
 * `data.affiliateRate` is a fraction 0–1 or null. `imageFile`/`ebookFile` optional File objects.
 */
export async function adminCreateProduct(data, imageFile = null, ebookFile = null) {
  const newRef = doc(collection(db, PRODUCTS));
  const productId = newRef.id;
  const email = adminEmail();

  let imageUrl = data.imageUrl || '';
  let imageStoragePath = '';
  if (imageFile) {
    const path = `marketplace/images/${productId}_${Date.now()}.${fileExt(imageFile, 'jpg')}`;
    const r = storageRef(storage, path);
    await uploadBytes(r, imageFile, { contentType: imageFile.type || 'image/jpeg', cacheControl: 'public, max-age=31536000, immutable' });
    imageUrl = await getDownloadURL(r);
    imageStoragePath = path;
  }

  await setDoc(newRef, {
    title: (data.title || '').trim(),
    description: (data.description || '').trim(),
    imageUrl,
    imageStoragePath,
    category: data.category,
    fulfillment: data.fulfillment,
    pricingMode: data.pricingMode,
    price: Number(data.price) || 0,
    originalPrice: Number(data.originalPrice) || 0,
    currency: 'MYR',
    status: data.status || 'draft',
    stock: data.stock === '' || data.stock == null ? null : Number(data.stock),
    // Tempoh akses dalam HARI. null = milik selamanya (cth akaun TikTok jual putus).
    durationDays: data.durationDays === '' || data.durationDays == null ? null : Number(data.durationDays),
    affiliateEnabled: !!data.affiliateEnabled,
    affiliateRate:
      data.affiliateRate === '' || data.affiliateRate == null ? null : Number(data.affiliateRate),
    order: Number(data.order) || 0,
    createdBy: email,
    updatedBy: email,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });

  if (ebookFile) {
    await uploadEbook(productId, ebookFile, data.internalNotes || '');
  } else if (data.internalNotes) {
    await setDoc(
      doc(db, PRODUCTS, productId, 'private', 'data'),
      { internalNotes: data.internalNotes, updatedBy: email, updatedAt: serverTimestamp() },
      { merge: true },
    );
  }

  return productId;
}

/**
 * Patch a product's public fields. `patch` should already use sen + fraction.
 *
 * ⚠️ Medan nombor ditapis di sini kerana borang admin menghantarnya sebagai TEKS
 *    (`<input type="number">` sentiasa memulangkan string). Tanpa penukaran ini
 *    Firestore menyimpan `stock: "24"`, dan semakan `typeof stock === 'number'`
 *    di server terlepas — stok tidak pernah berkurang selepas produk diedit.
 */
export async function adminUpdateProduct(productId, patch) {
  const clean = { ...patch };
  const toNumOrNull = (v) => (v === '' || v == null ? null : Number(v));
  if ('stock' in clean) clean.stock = toNumOrNull(clean.stock);
  if ('durationDays' in clean) clean.durationDays = toNumOrNull(clean.durationDays);
  if ('order' in clean) clean.order = Number(clean.order) || 0;
  await updateDoc(doc(db, PRODUCTS, productId), {
    ...clean,
    updatedBy: adminEmail(),
    updatedAt: serverTimestamp(),
  });
}

/** Replace the product image; updates imageUrl + imageStoragePath. */
export async function adminReplaceImage(productId, file) {
  const path = `marketplace/images/${productId}_${Date.now()}.${fileExt(file, 'jpg')}`;
  const r = storageRef(storage, path);
  await uploadBytes(r, file, { contentType: file.type || 'image/jpeg', cacheControl: 'public, max-age=31536000, immutable' });
  const imageUrl = await getDownloadURL(r);
  await adminUpdateProduct(productId, { imageUrl, imageStoragePath: path });
  return imageUrl;
}

/** Upload/replace the ebook PDF into the private subdoc. */
export async function uploadEbook(productId, file, internalNotes) {
  const path = `marketplace/ebooks/${productId}_${Date.now()}.pdf`;
  await uploadBytes(storageRef(storage, path), file);
  const payload = {
    ebookStoragePath: path,
    updatedBy: adminEmail(),
    updatedAt: serverTimestamp(),
  };
  if (typeof internalNotes === 'string') payload.internalNotes = internalNotes;
  await setDoc(doc(db, PRODUCTS, productId, 'private', 'data'), payload, { merge: true });
  return path;
}

/** Delete a product + its private subdoc + stok akaun (best-effort storage cleanup). */
export async function adminDeleteProduct(product) {
  const productId = typeof product === 'string' ? product : product.id;
  // Best-effort: remove image object
  if (product?.imageStoragePath) {
    await deleteObject(storageRef(storage, product.imageStoragePath)).catch(() => {});
  }
  // Subcollection TIDAK terpadam sendiri bila doc induk dipadam — tanpa langkah ini
  // teks kredential kekal tertinggal dalam Firestore selepas produk hilang dari UI.
  const items = await getDocs(stockCol(productId)).catch(() => null);
  if (items && !items.empty) {
    const batch = writeBatch(db);
    items.docs.forEach((d) => batch.delete(d.ref));
    await batch.commit().catch(() => {});
  }
  await deleteDoc(doc(db, PRODUCTS, productId, 'private', 'data')).catch(() => {});
  await deleteDoc(doc(db, PRODUCTS, productId));
}

// ═══════════════════════════════════════════
// STOK AKAUN — untuk produk `fulfillment: 'auto_stock'`
// ═══════════════════════════════════════════
// Satu doc = satu akaun sebenar (gmail + password + 2FA…). Teks kredential duduk
// DI SINI SAHAJA. Rules menolak semua bacaan klien kecuali admin; pembeli menerima
// teks itu melalui callable `getMarketplaceAccess` yang menyemak pemilik dan tempoh
// akses dahulu.
//
// ⛔ JANGAN salin medan `content` ke doc lain yang pembeli boleh baca terus
//    (contohnya `marketplace_orders/{id}/delivery/data`) — itu membatalkan seluruh
//    semakan tamat tempoh, kerana pembeli boleh terus membaca doc itu selama-lamanya.

const STOCK = 'stock_items';

function stockCol(productId) {
  return collection(db, PRODUCTS, productId, STOCK);
}

/**
 * Pecahkan satu tampalan besar kepada blok akaun.
 * Pemisah: baris kosong ATAU baris bergaris (`---`). Kedua-duanya diterima kerana
 * admin menampal daripada Sheet/nota dengan tabiat berbeza.
 */
export function parseStockBlocks(text) {
  return String(text || '')
    .replace(/\r\n/g, '\n')
    .split('\n')
    .map((line) => (/^\s*[-_=]{3,}\s*$/.test(line) ? '' : line))
    .join('\n')
    .split(/\n\s*\n/)
    .map((b) => b.trim())
    .filter(Boolean);
}

/** Label ringkas untuk senarai admin — baris pertama yang berisi, dipotong. */
function stockLabel(content) {
  const first = String(content || '').split('\n').find((l) => l.trim()) || '';
  return first.trim().slice(0, 60);
}

/** Admin: semua item stok bagi satu produk (tersedia + terjual). */
export async function adminListStockItems(productId) {
  const snap = await getDocs(query(stockCol(productId), orderBy('createdAt', 'asc')));
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}

/** Admin: tambah beberapa akaun sekaligus. Pulangkan bilangan yang ditambah. */
export async function adminAddStockItems(productId, blocks) {
  const list = (blocks || []).map((b) => String(b).trim()).filter(Boolean);
  if (!list.length) return 0;
  const email = adminEmail();
  const batch = writeBatch(db);
  list.forEach((content) => {
    batch.set(doc(stockCol(productId)), {
      content,
      label: stockLabel(content),
      status: 'available',
      orderId: null,
      buyerId: null,
      buyerEmail: null,
      soldAt: null,
      accessExpiresAt: null,
      createdBy: email,
      createdAt: serverTimestamp(),
    });
  });
  await batch.commit();
  await adminSyncStockCount(productId);
  return list.length;
}

/** Admin: padam satu item — hanya yang BELUM diserahkan kepada pembeli. */
export async function adminDeleteStockItem(productId, itemId) {
  const ref = doc(db, PRODUCTS, productId, STOCK, itemId);
  const snap = await getDoc(ref);
  if (snap.exists() && snap.data().status === 'sold') {
    throw new Error('Akaun ini sudah diserahkan kepada pembeli — tidak boleh dipadam.');
  }
  await deleteDoc(ref);
  await adminSyncStockCount(productId);
}

/**
 * Selaraskan `product.stock` dengan bilangan akaun yang masih tersedia.
 * Nombor stok bagi produk auto-serah TIDAK ditaip admin — ia cerminan senarai
 * sebenar, supaya kedai tidak boleh menjual lebih daripada akaun yang ada.
 * Produk yang kehabisan ditanda `sold_out`, dan kembali `published` sebaik
 * stok ditambah semula (supaya admin tidak perlu ingat menukarnya sendiri).
 */
export async function adminSyncStockCount(productId) {
  const snap = await getDocs(query(stockCol(productId), where('status', '==', 'available')));
  const available = snap.size;
  const prodSnap = await getDoc(doc(db, PRODUCTS, productId));
  const status = prodSnap.exists() ? prodSnap.data().status : null;
  const patch = { stock: available };
  if (available === 0 && status === 'published') patch.status = 'sold_out';
  if (available > 0 && status === 'sold_out') patch.status = 'published';
  await adminUpdateProduct(productId, patch);
  return available;
}

// ═══════════════════════════════════════════
// ORDERS
// ═══════════════════════════════════════════

/** Buyer uploads a payment proof to their own folder; returns the download URL. */
export async function uploadOrderProof(file) {
  const user = auth.currentUser;
  if (!user) throw new Error('Sila log masuk.');
  const path = `users/${user.uid}/marketplace_proofs/${Date.now()}.${fileExt(file, 'jpg')}`;
  const r = storageRef(storage, path);
  await uploadBytes(r, file);
  return getDownloadURL(r);
}

/**
 * Create a PAID order (status 'requested'). Reads the affiliate ref cookie for
 * last-click attribution. Returns the new order id.
 */
export async function createOrder(product, { whatsapp = '', proofImageUrl = null } = {}) {
  const user = auth.currentUser;
  if (!user) throw new Error('Sila log masuk.');
  const affiliateRef = getRefCookie() || null;

  // ⚠️ KEMBAR: firestore.rules marketplace_orders `keys().hasOnly([...])` menyenaraikan
  //    medan ini satu persatu — medan baharu di sini WAJIB ditambah di sana juga.
  const orderData = {
    buyerId: user.uid,
    buyerEmail: user.email || '',
    buyerWhatsapp: whatsapp || '',
    productId: product.id,
    productTitle: product.title || '',
    category: product.category,
    fulfillment: product.fulfillment,
    pricingMode: product.pricingMode,
    amount: Number(product.price) || 0,
    status: 'requested',
    paymentMethod: null,
    proofImageUrl: proofImageUrl || null,
    affiliateRef,
    commissionStatus: 'none',
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  };
  const created = await addDoc(collection(db, ORDERS), orderData);
  return created.id;
}

/**
 * Buyer: mula bayaran Billplz. Server yang mencipta pesanan DAN bil — harga
 * dibaca daripada Firestore, bukan dihantar dari sini.
 * Pulangan `{ url, orderId }`; pemanggil hantar pelanggan ke `url`.
 */
export async function createMarketplaceBill(product, { whatsapp = '', qty = 1 } = {}) {
  return createMarketplaceCartBill([{ productId: product.id, qty }], { whatsapp });
}

/**
 * Buyer: mula bayaran Billplz untuk SATU ATAU BANYAK barang (troli).
 * `items` = [{ productId, qty }]. Server membaca harga setiap barang daripada
 * Firestore dan mengira jumlahnya sendiri — jumlah TIDAK pernah dihantar dari sini.
 *
 * ⚠️ `productId` turut dihantar (barang PERTAMA) sebagai jaring: server versi
 *    lama tidak mengenali `items`, dan tanpa medan itu ia akan menolak permintaan
 *    dengan "productId diperlukan" — pelanggan nampak checkout rosak, bukan sekadar
 *    ciri troli yang belum ada.
 */
export async function createMarketplaceCartBill(items, { whatsapp = '' } = {}) {
  const user = auth.currentUser;
  if (!user) throw new Error('Sila log masuk.');
  const senarai = (items || [])
    .map((b) => ({ productId: String(b?.productId || ''), qty: Math.max(1, Math.floor(Number(b?.qty) || 1)) }))
    .filter((b) => b.productId);
  if (!senarai.length) throw new Error('Troli kosong.');
  const fn = httpsCallable(functions, 'createMarketplaceBill');
  const res = await fn({
    items: senarai,
    productId: senarai[0].productId,
    whatsapp,
    affiliateRef: getRefCookie() || null,
    returnOrigin: window.location.origin,
  });
  return res.data;
}

/**
 * Baris dalam satu pesanan — SATU bentuk untuk pesanan lama dan pesanan troli.
 *
 * Pesanan troli membawa `lines[]`. Pesanan LAMA (sebelum troli) hanya membawa
 * satu produk pada aras atas doc. Helper ini menyeragamkan kedua-duanya supaya
 * halaman pembeli dan panel admin tidak perlu tahu bezanya.
 *
 * ⚠️ Ia adalah kembar kepada `barisPesanan` di server (functions/index.js).
 *    Kalau bentuk baris ditukar di satu tempat, tukar di kedua-duanya — kalau
 *    tidak, skrin akan menunjukkan sesuatu yang berbeza daripada apa yang
 *    sebenarnya diserahkan.
 */
export function barisPesanan(order) {
  if (Array.isArray(order?.lines) && order.lines.length) {
    return order.lines.map((l, i) => ({
      idx: typeof l.idx === 'number' ? l.idx : i,
      productId: l.productId || '',
      productTitle: l.productTitle || '',
      category: l.category || '',
      fulfillment: l.fulfillment || '',
      unitPrice: Number(l.unitPrice) || 0,
      qty: Math.max(1, Number(l.qty) || 1),
      lineTotal: Number(l.lineTotal) || 0,
      stockItemIds: Array.isArray(l.stockItemIds) ? l.stockItemIds : [],
      delivered: Number(l.delivered) || 0,
      shortfall: Number(l.shortfall) || 0,
    }));
  }
  return [{
    idx: 0,
    productId: order?.productId || '',
    productTitle: order?.productTitle || '',
    category: order?.category || '',
    fulfillment: order?.fulfillment || '',
    unitPrice: Number(order?.amount) || 0,
    qty: 1,
    lineTotal: Number(order?.amount) || 0,
    stockItemIds: order?.stockItemId ? [order.stockItemId] : [],
    delivered: order?.stockItemId ? 1 : 0,
    shortfall: 0,
  }];
}

/** Buyer's own orders, newest first (the "Pembelian Saya" locker). */
export async function listMyOrders() {
  const user = auth.currentUser;
  if (!user) return [];
  const q = query(
    collection(db, ORDERS),
    where('buyerId', '==', user.uid),
    orderBy('createdAt', 'desc'),
  );
  const snap = await getDocs(q);
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}

/** Read the delivered access info for one order (buyer of the order or admin). */
export async function getOrderDelivery(orderId) {
  const snap = await getDoc(doc(db, ORDERS, orderId, 'delivery', 'data'));
  return snap.exists() ? snap.data() : null;
}

/** Admin: list orders by status (default the pending queue). */
export async function adminListOrders(status = 'requested') {
  const q = query(
    collection(db, ORDERS),
    where('status', '==', status),
    orderBy('createdAt', 'desc'),
  );
  const snap = await getDocs(q);
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}

/** Admin: reject an order (no delivery, no commission). */
export async function adminRejectOrder(orderId, reason = '') {
  await updateDoc(doc(db, ORDERS, orderId), {
    status: 'rejected',
    rejectReason: reason,
    updatedBy: adminEmail(),
    updatedAt: serverTimestamp(),
  });
}

// ═══════════════════════════════════════════
// CALLABLE WRAPPERS (Cloud Functions)
// ═══════════════════════════════════════════

/** Returns a short-lived signed URL to download a delivered ebook order. */
export async function getMarketplaceDownloadUrl(orderId, lineIndex) {
  const fn = httpsCallable(functions, 'getMarketplaceDownload');
  // `lineIndex` menyatakan ebook YANG MANA dalam pesanan troli. Tanpa ia, server
  // mengambil ebook pertama — betul untuk pesanan satu barang, dan itulah yang
  // dihantar oleh hosting lama.
  const res = await fn(Number.isInteger(lineIndex) ? { orderId, lineIndex } : { orderId });
  return res.data; // { url, expiresInSec }
}

/** Admin: confirm payment + deliver access + credit affiliate commission. */
export async function markOrderDelivered(orderId, info) {
  const fn = httpsCallable(functions, 'markMarketplaceDelivered');
  const res = await fn({ orderId, info: info || '' });
  return res.data; // { success, commissionCredited, commissionAmountSen }
}

/**
 * Buyer: ambil kredential akaun bagi satu order auto-serah.
 * Server yang menyemak pemilik order + tempoh akses; klien tidak boleh membaca
 * `stock_items` secara terus (rules menolak).
 * Pulangan: `{ expired, expiresAt, content }` — `content` null bila sudah tamat.
 */
export async function getMarketplaceAccess(orderId) {
  const fn = httpsCallable(functions, 'getMarketplaceAccess');
  const res = await fn({ orderId });
  return res.data;
}

/** Buyer: claim a free ebook (server creates a delivered order). */
export async function claimFreeEbook(productId) {
  const fn = httpsCallable(functions, 'requestFreeEbook');
  const res = await fn({ productId });
  return res.data; // { orderId }
}
